import {
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import MinMaxFilter from '../../shared/MinMaxFilter';
import SliderFilter from '../../shared/SliderFilter';
import { useRecoilValue } from 'recoil';
import MultiOptionSelector from '../../shared/MultiOptionSelector';
import MultiOptionAutocomplete from '../../shared/MultiOptionAutocomplete';
import OptionsDropdownMultiSelector from '../../shared/OptionsDropdownSelector';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import DateTimeSelector from './DateTimeSelector';
import GroupedFiltersSection from './GroupedFiltersSection';
import {
  Aggressor,
  Filter,
  FilterOperator,
  FilterValueFormat,
  OptionsFeedColumnKey,
  OptionTradeSide,
} from '../../../types/tape';
import {
  TAPE_DEFAULT_FILTER_ID,
  TAPE_COLUMN_TOOLTIPS_MAP,
} from '../../../config/tape';
import {
  hiroManifestState,
  watchlistsState,
  timezoneState,
  unpricedEquitiesState,
} from '../../../states';
import { AnyEquity, ScannerState, ScannerID } from '../../../types';
import {
  getAllSymsFromManifest,
  generateExpirationTimestamps,
} from '../../../util';
import { dayjs, getDateFormatted } from 'util/shared/date';
import {
  addOrUpdateFilters,
  getFilterValue,
  getExpirationFilterItems,
} from '../../../util/tape';
import { SGTooltip } from '../../core';
import InfoPopper from '../../shared/InfoPopper';
import { SG_SPECIAL_SCANNERS } from 'config/equityhub';
import { SCANNERS } from 'config/scanners';
import { arraysEqual } from 'util/shared/common';

interface FilterPanelProps {
  filters: Filter[];
  onChangeFilters: (newFilters: Filter[]) => void;
  noSym?: boolean;
  viewOnly?: boolean;
  sx?: SxProps<Theme>;
}

const MIN_DAYS = 0;
const MAX_DAYS = 1_461; // 4 years

const MIN_PREM = 0;
const MAX_PREM = 1_000_000_000; // 1B

const minMaxFiltersConfig: {
  title: string;
  description?: string;
  format: FilterValueFormat;
  minId: TAPE_DEFAULT_FILTER_ID;
  maxId: TAPE_DEFAULT_FILTER_ID;
  field: OptionsFeedColumnKey;
}[] = [
  {
    title: 'PREMIUM',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Premium),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinPremium,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxPremium,
    field: OptionsFeedColumnKey.Premium,
  },
  {
    title: 'STRIKE',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Strike),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinStrike,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxStrike,
    field: OptionsFeedColumnKey.Strike,
  },
  {
    title: 'VOLUME',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(
      OptionsFeedColumnKey.DailyVolCumsum,
    ),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinVol,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxVol,
    field: OptionsFeedColumnKey.DailyVolCumsum,
  },
  {
    title: 'O/I',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.PrevOi),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinOI,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxOI,
    field: OptionsFeedColumnKey.PrevOi,
  },
  {
    title: 'SIZE',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Size),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinSize,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxSize,
    field: OptionsFeedColumnKey.Size,
  },
  {
    title: 'SPOT',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.StockPrice),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinSpotPrice,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxSpotPrice,
    field: OptionsFeedColumnKey.StockPrice,
  },
  {
    title: 'PRICE',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Price),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinOptPrice,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxOptPrice,
    field: OptionsFeedColumnKey.Price,
  },
  {
    title: 'DELTA',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Delta),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinDelta,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxDelta,
    field: OptionsFeedColumnKey.Delta,
  },
  {
    title: 'GAMMA',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Gamma),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinGamma,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxGamma,
    field: OptionsFeedColumnKey.Gamma,
  },
  {
    title: 'VEGA',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Vega),
    format: 'number',
    minId: TAPE_DEFAULT_FILTER_ID.MinVega,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxVega,
    field: OptionsFeedColumnKey.Vega,
  },
  {
    title: 'IV',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.IVol),
    format: 'percentage',
    minId: TAPE_DEFAULT_FILTER_ID.MinIV,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxIV,
    field: OptionsFeedColumnKey.IVol,
  },
  {
    title: 'BID',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Bid),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinBid,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxBid,
    field: OptionsFeedColumnKey.Bid,
  },
  {
    title: 'ASK',
    description: TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Ask),
    format: 'currency',
    minId: TAPE_DEFAULT_FILTER_ID.MinAsk,
    maxId: TAPE_DEFAULT_FILTER_ID.MaxAsk,
    field: OptionsFeedColumnKey.Ask,
  },
];

const MAIN_SCANNER_OPTIONS = SG_SPECIAL_SCANNERS.filter(
  (scanner) => scanner.scanner !== ScannerID.HIGHEST_OPTIONS_IMPACT, // TODO: Bring back Highest Options Impact scanner once the fields are available
).map((scanner) => ({
  label: scanner.label.name,
  value: scanner.scanner,
  icon: scanner.label.icon,
}));

const FilterPanel = memo(
  ({ filters, onChangeFilters, noSym, viewOnly, sx }: FilterPanelProps) => {
    const manifest = useRecoilValue(hiroManifestState);
    const watchlists = useRecoilValue(watchlistsState);
    const equities = useRecoilValue(unpricedEquitiesState);
    const userTimezone = useRecoilValue(timezoneState);
    const [now, setNow] = useState(dayjs().tz(userTimezone).valueOf());

    const [watchlistStates, setWatchlistStates] = useState(
      new Map<string, ScannerState>(),
    );
    const [scannerStates, setScannerStates] = useState(
      new Map<ScannerID, ScannerState>(),
    );
    const [manualAdditions, setManualAdditions] = useState(
      () =>
        new Set<string>(
          getFilterValue<string[]>(filters, TAPE_DEFAULT_FILTER_ID.Symbols, []),
        ),
    );
    const [manualRemovals, setManualRemovals] = useState(new Set<string>());

    const selectedScannerIds = useMemo(() => {
      return [...scannerStates.entries()]
        .filter(([_, s]) => s === ScannerState.Selected)
        .map(([id, _]) => id);
    }, [scannerStates]);

    const selectedWatchlistIds = useMemo(() => {
      return [...watchlistStates.entries()]
        .filter(([_, s]) => s === ScannerState.Selected)
        .map(([id, _]) => id);
    }, [watchlistStates]);

    const validTnsSyms = useMemo(
      () => getAllSymsFromManifest(manifest),
      [manifest],
    );

    const onManualSymAddition = useCallback((sym: string) => {
      setManualAdditions((prev) => new Set(prev).add(sym));
      setManualRemovals((prev) => (prev.delete(sym) ? new Set(prev) : prev));
    }, []);

    const onManualSymDeletion = useCallback(
      (sym: string) => {
        setScannerStates((prev) => {
          const newStates: [ScannerID, ScannerState][] = [];
          for (const [id, state] of prev.entries()) {
            if (
              state === ScannerState.Selected &&
              SCANNERS.get(id)!.passes(equities.get(sym)!)
            ) {
              newStates.push([id, ScannerState.Active]);
            }
          }
          return newStates.length === 0
            ? prev
            : newStates.reduce(
                (acc, [id, state]) => acc.set(id, state),
                new Map(prev),
              );
        });
        setWatchlistStates((prev) => {
          // Any watchlists that are currently selected and *not* present in newSelectedIds should be marked Off
          for (const watchlist of watchlists ?? []) {
            const state =
              watchlist.id != null ? prev.get(String(watchlist.id)) : null;
            if (state == null || state === ScannerState.Active) {
              continue;
            }
            prev = new Map(prev.set(String(watchlist.id), ScannerState.Active));
          }
          return prev;
        });
        setManualRemovals((prev) => new Set(prev).add(sym));
        setManualAdditions((prev) => (prev.delete(sym) ? new Set(prev) : prev));
      },
      [equities, setScannerStates, watchlists],
    );

    // Update the "currentTime" every 60 seconds
    useEffect(() => {
      const interval = setInterval(() => {
        setNow(dayjs().tz(userTimezone).valueOf()); // Update the "current time" as number in user's timezone
      }, 60_000); // 60 seconds interval

      return () => clearInterval(interval); // Cleanup on component unmount
    }, [userTimezone]); // Depend on userTimezone to recompute if the user's timezone changes

    const onFilterChange = useCallback(
      (filter: Filter) => {
        const updatedFilters = addOrUpdateFilters(filters, [filter]);
        onChangeFilters(updatedFilters);
      },
      [filters, onChangeFilters],
    );

    const onSliderFilterChange = useCallback(
      (
        ids: TAPE_DEFAULT_FILTER_ID[],
        field: OptionsFeedColumnKey,
        newValue: number | number[],
      ) => {
        const filtersToUpdate = Array.isArray(newValue)
          ? [
              {
                id: ids[0],
                field,
                operator: FilterOperator.GreaterThanOrEqual,
                value: newValue[0],
              },
              {
                id: ids[1],
                field,
                operator: FilterOperator.LessThanOrEqual,
                value: newValue[1],
              },
            ]
          : [
              {
                id: ids[0],
                field,
                operator: FilterOperator.GreaterThanOrEqual,
                value: newValue,
              },
            ];

        const updatedFilters = addOrUpdateFilters(filters, filtersToUpdate);
        onChangeFilters(updatedFilters);
      },
      [filters, onChangeFilters],
    );

    const onScannerWatchlistSelectChange = useCallback(
      (
        event: SelectChangeEvent<string | string[]>,
        type: 'scanner' | 'watchlist',
      ) => {
        const newSelectedIds = event.target.value as string[];
        if (type === 'scanner') {
          setScannerStates((prev) => {
            // Any scanners that are currently selected and *not* present in newSelectedIds should be marked Off
            for (const [i, state] of prev) {
              if (
                !newSelectedIds.includes(i) &&
                state !== ScannerState.Active
              ) {
                prev.delete(i);
              }
            }
            return newSelectedIds.reduce(
              (acc, i) => acc.set(i as ScannerID, ScannerState.Selected),
              new Map(prev),
            );
          });

          // For any potentially new selected scanners, remove their symbols from manual removals
          setManualRemovals((prev) => {
            const cancelRemovals = new Set(
              [...prev].filter((sym) =>
                newSelectedIds.some((id) => {
                  const equity = equities.get(sym);
                  return (
                    equity != null &&
                    (SCANNERS.get(id as ScannerID)?.passes(equity) ?? false)
                  );
                }),
              ),
            );

            return cancelRemovals.size === 0
              ? prev
              : [...cancelRemovals].reduce((acc: Set<string>, s: string) => {
                  acc.delete(s);
                  return acc;
                }, new Set(prev));
          });
        } else {
          setWatchlistStates((prev) => {
            // Any watchlists that are currently selected and *not* present in newSelectedIds should be marked Off
            for (const [i, state] of prev) {
              if (
                !newSelectedIds.includes(i) &&
                state !== ScannerState.Active
              ) {
                prev.delete(i);
              }
            }
            return newSelectedIds.reduce(
              (acc, i) => acc.set(i, ScannerState.Selected),
              new Map(prev),
            );
          });

          // For any potentially new selected watchlists, remove their symbols from manual removals
          const selectedWatchlists = (watchlists ?? []).filter(
            (w) => w.id != null && newSelectedIds.includes(String(w.id)),
          );
          setManualRemovals((prev) => {
            const cancelRemovals = new Set(
              [...prev].filter((s) =>
                selectedWatchlists.some((w) => w.symbols.includes(s)),
              ),
            );
            return cancelRemovals.size === 0
              ? prev
              : [...cancelRemovals].reduce((acc: Set<string>, s: string) => {
                  acc.delete(s);
                  return acc;
                }, new Set(prev));
          });
        }
      },
      [setScannerStates, setWatchlistStates, watchlists, equities],
    );

    useEffect(() => {
      const enabledScanners = [...scannerStates.keys()].map(
        (id) => SCANNERS.get(id)!,
      );
      const enabledWatchlists = new Set(
        [...watchlistStates.keys()].map(Number),
      );
      const enabledWatchlistSyms = new Set(
        (
          watchlists?.filter(
            (w) => w.id != null && enabledWatchlists.has(w.id),
          ) ?? []
        ).flatMap((w) => w.symbols),
      );
      const enabledSyms = validTnsSyms.filter((sym) => {
        const equity: AnyEquity | undefined = equities.get(sym);
        return (
          (manualAdditions.has(sym) ||
            (equity != null &&
              enabledScanners.reduce(
                (acc, s) => acc || s.passes(equity),
                false,
              )) ||
            enabledWatchlistSyms.has(sym)) &&
          !manualRemovals.has(sym)
        );
      });

      const prevSyms = getFilterValue<string[]>(
        filters,
        TAPE_DEFAULT_FILTER_ID.Symbols,
        [],
      );

      if (!arraysEqual(enabledSyms.slice().sort(), prevSyms.slice().sort())) {
        onChangeFilters(
          addOrUpdateFilters(filters, [
            {
              id: TAPE_DEFAULT_FILTER_ID.Symbols,
              field: OptionsFeedColumnKey.Underlying,
              operator: FilterOperator.IsAnyOf,
              value: enabledSyms,
            },
          ]),
        );
      }
    }, [
      equities,
      filters,
      manualAdditions,
      manualRemovals,
      onChangeFilters,
      scannerStates,
      validTnsSyms,
      watchlistStates,
      watchlists,
    ]);

    // Helper function to handle filter changes for min values
    const handleMinChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: OptionsFeedColumnKey,
      minId: TAPE_DEFAULT_FILTER_ID,
    ) => {
      onChangeFilters(
        addOrUpdateFilters(filters, [
          {
            field,
            value: event.target.value,
            id: minId,
            operator: FilterOperator.GreaterThanOrEqual,
          },
        ]),
      );
    };

    // Helper function to handle filter changes for max values
    const handleMaxChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: OptionsFeedColumnKey,
      maxId: TAPE_DEFAULT_FILTER_ID,
    ) => {
      onChangeFilters(
        addOrUpdateFilters(filters, [
          {
            field,
            value: event.target.value,
            id: maxId,
            operator: FilterOperator.LessThanOrEqual,
          },
        ]),
      );
    };

    const validateDateTimeChange = (newValue: number | null): boolean => {
      if (newValue === null) {
        return true;
      }

      // Ensure newValue is not in the future
      if (newValue > now) {
        console.warn('Selected datetime cannot be in the future.');
        return false;
      }

      return true; // Valid datetime change
    };

    const handleMinDateTimeChange = (
      field: OptionsFeedColumnKey,
      newValue: number | null,
      id: TAPE_DEFAULT_FILTER_ID,
    ) => {
      // Validate the new "From" datetime before applying the change
      if (validateDateTimeChange(newValue)) {
        onChangeFilters(
          addOrUpdateFilters(filters, [
            {
              field,
              value: newValue,
              id,
              operator: FilterOperator.GreaterThanOrEqual,
            },
          ]),
        );
      }
    };

    const handleMaxDateTimeChange = (
      field: OptionsFeedColumnKey,
      newValue: number | null,
      id: TAPE_DEFAULT_FILTER_ID,
    ) => {
      // Validate the new "To" datetime before applying the change
      if (validateDateTimeChange(newValue)) {
        onChangeFilters(
          addOrUpdateFilters(filters, [
            {
              field,
              value: newValue,
              id,
              operator: FilterOperator.LessThanOrEqual,
            },
          ]),
        );
      }
    };

    const fromDateTime = getFilterValue<number | null>(
      filters,
      TAPE_DEFAULT_FILTER_ID.MinDateTime,
      null,
    );

    const toDateTime = getFilterValue<number | null>(
      filters,
      TAPE_DEFAULT_FILTER_ID.MaxDateTime,
      null,
    );

    const memoizedMarketDays = useMemo(
      () =>
        generateExpirationTimestamps(
          fromDateTime != null ? dayjs(fromDateTime) : dayjs(now),
        ),
      [fromDateTime, now],
    );

    return (
      <Stack
        sx={{
          gap: 5,
          ...sx,
        }}
      >
        <Stack sx={{ width: '100%', gap: 1 }}>
          <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <SGTooltip
              title={TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Time)}
            >
              <Typography
                sx={{
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Timeframe
              </Typography>
            </SGTooltip>

            <InfoPopper
              contents={[
                {
                  label: 'From and To Timeframes',
                  description:
                    "The data displayed is on a maximum of 24 hours basis, where 'to' time uses the last market close time.",
                },
              ]}
            />
          </Stack>
          <Stack sx={{ gap: 3 }}>
            <DateTimeSelector
              label="From"
              value={fromDateTime}
              onChange={(newValue: number | null): void =>
                handleMinDateTimeChange(
                  OptionsFeedColumnKey.Time,
                  newValue,
                  TAPE_DEFAULT_FILTER_ID.MinDateTime,
                )
              }
              min={dayjs('2024-10-24').valueOf()}
              max={now} // Max is "right now"
              disabled={viewOnly}
            />
            <DateTimeSelector
              label="To"
              value={toDateTime}
              onChange={(newValue: number | null): void =>
                handleMaxDateTimeChange(
                  OptionsFeedColumnKey.Time,
                  newValue,
                  TAPE_DEFAULT_FILTER_ID.MaxDateTime,
                )
              }
              min={dayjs('2024-10-24').valueOf()}
              max={now} // Max is "right now"
              disabled={viewOnly}
            />
          </Stack>
        </Stack>

        {!(noSym ?? false) && (
          <>
            <MultiOptionAutocomplete
              options={validTnsSyms}
              value={getFilterValue<string[]>(
                filters,
                TAPE_DEFAULT_FILTER_ID.Symbols,
                [],
              )}
              onChange={(_event: React.SyntheticEvent, value: string[]) => {
                const prevSymbols = getFilterValue<string[]>(
                  filters,
                  TAPE_DEFAULT_FILTER_ID.Symbols,
                  [],
                );
                const addedSymbols = value.filter(
                  (v) => !prevSymbols.includes(v),
                );
                const removedSymbols = prevSymbols.filter(
                  (v) => !value.includes(v),
                );
                addedSymbols.forEach((symbol) => onManualSymAddition(symbol));
                removedSymbols.forEach((symbol) => onManualSymDeletion(symbol));
              }}
              onItemDelete={(item: string) => {
                onManualSymDeletion(item);
              }}
              onItemAdd={(item: string) => {
                onManualSymAddition(item);
              }}
              viewOnly={viewOnly}
            />
            <Stack
              sx={{
                gap: 2,
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <OptionsDropdownMultiSelector
                label="Watchlist"
                value={selectedWatchlistIds}
                options={
                  watchlists?.map((w) => ({
                    label: w.name,
                    value: `${w.id!}`,
                  })) ?? []
                }
                isMultiple
                onChange={(event) =>
                  onScannerWatchlistSelectChange(event, 'watchlist')
                }
                viewOnly={viewOnly}
                sx={{ maxWidth: '100%' }}
              />
              <OptionsDropdownMultiSelector
                label="Scanner"
                value={selectedScannerIds}
                viewOnly={viewOnly}
                options={MAIN_SCANNER_OPTIONS}
                isMultiple
                onChange={(event) =>
                  onScannerWatchlistSelectChange(event, 'scanner')
                }
                sx={{ maxWidth: '100%' }}
              />
            </Stack>
          </>
        )}
        <MultiOptionAutocomplete
          options={memoizedMarketDays}
          placeholder="Select expiration dates"
          optionLabelFormatter={(val: string) =>
            getDateFormatted(parseInt(val))
          }
          value={getFilterValue<string[]>(
            filters,
            TAPE_DEFAULT_FILTER_ID.Expirations,
            [],
          )}
          onChange={(_event: React.SyntheticEvent, value: string[]) => {
            const updatedFilters = addOrUpdateFilters(
              filters,
              getExpirationFilterItems(value),
            );
            onChangeFilters(updatedFilters);
          }}
          viewOnly={viewOnly}
          listBoxProps={{
            maxWidth: '100%',
            height: 300,
          }}
        />

        <SliderFilter
          viewOnly={viewOnly}
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(
            OptionsFeedColumnKey.Expiry,
          )}
          disabled={
            getFilterValue<string[]>(
              filters,
              TAPE_DEFAULT_FILTER_ID.Expirations,
              [],
            )?.length > 0
          }
          header="EXPIRATION"
          name="expiration"
          value={[
            getFilterValue<number>(
              filters,
              TAPE_DEFAULT_FILTER_ID.MinExp,
              MIN_DAYS,
            ),
            getFilterValue<number>(
              filters,
              TAPE_DEFAULT_FILTER_ID.MaxExp,
              MAX_DAYS,
            ),
          ]}
          min={MIN_DAYS}
          max={MAX_DAYS}
          valueFormat="date"
          onChange={(newValue: number | number[]) =>
            onSliderFilterChange(
              [TAPE_DEFAULT_FILTER_ID.MinExp, TAPE_DEFAULT_FILTER_ID.MaxExp],
              OptionsFeedColumnKey.Expiry,
              newValue,
            )
          }
          useLogScale
        />
        <SliderFilter
          viewOnly={viewOnly}
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(
            OptionsFeedColumnKey.Premium,
          )}
          header="PREMIUM"
          name="premium"
          value={getFilterValue<number>(
            filters,
            TAPE_DEFAULT_FILTER_ID.MinPremium,
            0,
          )}
          min={MIN_PREM}
          max={MAX_PREM}
          valueFormat="currency"
          onChange={(newValue: number | number[]) =>
            onSliderFilterChange(
              [TAPE_DEFAULT_FILTER_ID.MinPremium],
              OptionsFeedColumnKey.Premium,
              newValue,
            )
          }
          useLogScale
        />
        <MultiOptionSelector
          viewOnly={viewOnly}
          title="SIDE"
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(
            OptionsFeedColumnKey.TradeSide,
          )}
          options={Object.values(OptionTradeSide).map((ots) => ({
            label: ots.toUpperCase(),
            value: ots,
          }))}
          selectedOptions={getFilterValue<string[]>(
            filters,
            TAPE_DEFAULT_FILTER_ID.Side,
            [],
          )}
          onSelect={(selectedOptions: string[] | string | boolean) =>
            onFilterChange({
              id: TAPE_DEFAULT_FILTER_ID.Side,
              value: selectedOptions as string[],
              operator: FilterOperator.IsAnyOf,
              field: OptionsFeedColumnKey.TradeSide,
            })
          }
        />
        <MultiOptionSelector
          viewOnly={viewOnly}
          singleSelect
          title="AGRESSOR"
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(
            OptionsFeedColumnKey.Aggressor,
          )}
          options={[Aggressor.BUY, Aggressor.SELL].map((a) => ({
            label: a,
            value: a,
          }))}
          selectedOptions={getFilterValue<string>(
            filters,
            TAPE_DEFAULT_FILTER_ID.Aggressor,
            '',
          )}
          onSelect={(selectedOption: string[] | string | boolean) =>
            onFilterChange({
              id: OptionsFeedColumnKey.Aggressor,
              value: selectedOption as string,
              operator: FilterOperator.Equal,
              field: OptionsFeedColumnKey.Aggressor,
            })
          }
        />
        <MultiOptionSelector
          viewOnly={viewOnly}
          singleSelect
          title="CALL/PUT"
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.IsPut)}
          options={[
            {
              label: 'Put',
              value: true,
            },
            {
              label: 'Call',
              value: false,
            },
          ]}
          selectedOptions={getFilterValue<boolean | ''>(
            filters,
            TAPE_DEFAULT_FILTER_ID.IsPut,
            '',
          )}
          onSelect={(selectedOption: string[] | string | boolean) =>
            onFilterChange({
              id: TAPE_DEFAULT_FILTER_ID.IsPut,
              value: selectedOption as boolean | null,
              operator: FilterOperator.Equal,
              field: OptionsFeedColumnKey.IsPut,
            })
          }
        />
        {minMaxFiltersConfig.map(
          ({ title, description, format, minId, maxId, field }) => (
            <MinMaxFilter
              key={`${title}-${minId}-${maxId}`}
              viewOnly={viewOnly}
              title={title}
              description={description}
              format={format}
              minValue={getFilterValue<string>(filters, minId, '')}
              maxValue={getFilterValue<string>(filters, maxId, '')}
              minPlaceholder={
                format === 'currency'
                  ? '$0'
                  : format === 'percentage'
                    ? '0%'
                    : '0'
              }
              maxPlaceholder={
                format === 'currency'
                  ? '$∞'
                  : format === 'percentage'
                    ? '∞%'
                    : '∞'
              }
              onChangeMin={(event) => handleMinChange(event, field, minId)}
              onChangeMax={(event) => handleMaxChange(event, field, maxId)}
            />
          ),
        )}
        <GroupedFiltersSection
          title="Flags"
          description={TAPE_COLUMN_TOOLTIPS_MAP.get(OptionsFeedColumnKey.Flags)}
        >
          <Stack sx={{ gap: 2 }}>
            <MultiOptionSelector
              viewOnly={viewOnly}
              singleSelect
              title="SWEEP"
              options={BOOLEAN_OPTS}
              selectedOptions={getFilterValue<boolean | ''>(
                filters,
                TAPE_DEFAULT_FILTER_ID.IsSweep,
                '',
              )}
              onSelect={(selectedOption: string[] | string | boolean) =>
                onFilterChange({
                  id: TAPE_DEFAULT_FILTER_ID.IsSweep,
                  value: selectedOption as boolean | null,
                  operator: FilterOperator.Equal,
                  field: OptionsFeedColumnKey.IsSweep,
                })
              }
            />
            <MultiOptionSelector
              viewOnly={viewOnly}
              singleSelect
              title="CROSS"
              options={BOOLEAN_OPTS}
              selectedOptions={getFilterValue<boolean | ''>(
                filters,
                TAPE_DEFAULT_FILTER_ID.IsCross,
                '',
              )}
              onSelect={(selectedOption: string[] | string | boolean) =>
                onFilterChange({
                  id: TAPE_DEFAULT_FILTER_ID.IsCross,
                  value: selectedOption as boolean | null,
                  operator: FilterOperator.Equal,
                  field: OptionsFeedColumnKey.IsCross,
                })
              }
            />
            <MultiOptionSelector
              viewOnly={viewOnly}
              singleSelect
              title="BLOCK"
              options={BOOLEAN_OPTS}
              selectedOptions={getFilterValue<boolean | ''>(
                filters,
                TAPE_DEFAULT_FILTER_ID.IsBlock,
                '',
              )}
              onSelect={(selectedOption: string[] | string | boolean) =>
                onFilterChange({
                  id: TAPE_DEFAULT_FILTER_ID.IsBlock,
                  value: selectedOption as boolean | null,
                  operator: FilterOperator.Equal,
                  field: OptionsFeedColumnKey.IsBlock,
                })
              }
            />
            <MultiOptionSelector
              viewOnly={viewOnly}
              singleSelect
              title="MULTI LEG"
              options={BOOLEAN_OPTS}
              selectedOptions={getFilterValue<boolean | ''>(
                filters,
                TAPE_DEFAULT_FILTER_ID.IsSpread,
                '',
              )}
              onSelect={(selectedOption: string[] | string | boolean) =>
                onFilterChange({
                  id: TAPE_DEFAULT_FILTER_ID.IsSpread,
                  value: selectedOption as boolean | null,
                  operator: FilterOperator.Equal,
                  field: OptionsFeedColumnKey.IsSpread,
                })
              }
            />
          </Stack>
        </GroupedFiltersSection>
      </Stack>
    );
  },
);

const BOOLEAN_OPTS = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;
